/**
 * cartLogger.js
 *
 * Structured logger for all cart system events.
 *
 * Usage:
 *   import { logCartEvent } from '../utils/cartLogger';
 *   logCartEvent('cart_saved', { itemCount: 3, version: 5 });
 *
 * In development: emits a structured object to the console so events are
 *   easy to filter by type in DevTools (filter by "[cart]").
 *
 * In production: no-op by default. To enable remote monitoring, uncomment
 *   the sendToMonitoring() call at the bottom of logCartEvent. The endpoint
 *   must accept POST /api/internal/logs/cart with a JSON body.
 *
 * ── Event catalogue ──────────────────────────────────────────────────────────
 * cart_loaded            GET /api/cart complete           { itemCount, version }
 * cart_merged            Post-login local+DB merge         { localCount, dbCount, mergedCount }
 * cart_saved             PUT /api/cart success             { itemCount, version }
 * cart_conflict          409 detected before retry         { clientVersion, count }
 * cart_conflict_resolved PUT succeeded after 409 retry     { version }
 * cart_items_dropped     PUT 200 response has fewer items  { sent, saved, droppedCount, context }
 * high_conflict_rate     ≥3 conflicts in one session       { count }
 * merge_anomaly          merged > local+db (impossible by design, guard) { localCount, dbCount, mergedCount }
 * cart_size_exceeded     cart has >50 distinct items        { size }
 * invalid_items_detected sanitizeCart dropped items         { droppedCount, items }
 * api_failure            DB sync failed                    { error, failureCount }
 * fallback_mode_enabled  ≥3 consecutive API failures       { failureCount }
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Optional remote monitoring ────────────────────────────────────────────────
// Uncomment when a backend monitoring endpoint is available.
// const MONITORING_URL = '/api/internal/logs/cart';
//
// async function sendToMonitoring(event) {
//   try {
//     await fetch(MONITORING_URL, {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify(event),
//     });
//   } catch {
//     // silent fail — monitoring must never disrupt the cart
//   }
// }

/**
 * Log a cart system event.
 *
 * @param {string} type     - Event name (see catalogue above)
 * @param {object} payload  - Arbitrary key/value context for the event
 */
export function logCartEvent(type, payload = {}) {
  const event = {
    type,
    timestamp: new Date().toISOString(),
    ...payload,
  };

  if (process.env.NODE_ENV === 'development') {
    // Use console.error for failure/anomaly events so they surface in red;
    // use console.log for informational events.
    const isError = [
      'cart_conflict',
      'high_conflict_rate',
      'merge_anomaly',
      'cart_size_exceeded',
      'invalid_items_detected',
      'api_failure',
      'fallback_mode_enabled',
    ].includes(type);

    if (isError) {
      // eslint-disable-next-line no-console
      console.error('[cart]', event);
    } else {
      // eslint-disable-next-line no-console
      console.log('[cart]', event);
    }
  }

  // Uncomment to enable remote monitoring:
  // sendToMonitoring(event);
}
